import { useEffect, useMemo, useRef } from "react"
import { Alert, Box, Button, CircularProgress, Typography } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { GridViewOutlined } from "@mui/icons-material"
import { visuallyHidden } from "@mui/utils"
import { DataGridPremium, gridExpandedSortedRowIdsSelector, useGridApiRef } from "@mui/x-data-grid-premium"
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-premium"
import { useScheduledWorkflowDeliverables } from "resources/scheduled-workflows/hooks"
import type { ScheduledWorkflowDeliverableListItem } from "resources/scheduled-workflows/types"
import { reportEntryLabel, UNREAD_DOT_SX } from "../scheduled-workflows/report-format"
import { viewerTimeZone } from "../scheduled-workflows/schedule-format"
import { SectionMoreButton } from "./collapsible-section"
import { MessageTime } from "./message-time"
import { ArtifactsToolbar, CENTER_SX, EMPTY_BODY_SX, EMPTY_COPY, EMPTY_GLYPH_SX, EMPTY_SX, GRID_SX } from "./surface-styles"
import { useOpenReport } from "./use-open-report"

const VIEWER_ZONE = viewerTimeZone()
const UNREAD_NAME_SX = { display: "flex", alignItems: "center", gap: 1, minWidth: 0, height: "100%" } satisfies SxProps<Theme>
const UNREAD_TEXT_SX = { fontWeight: 700 } satisfies SxProps<Theme>
const TOOLBAR_PROPS = { placeholder: "Search reports…", ariaLabel: "Search reports" }
const EMPTY_DELIVERABLES: ScheduledWorkflowDeliverableListItem[] = []

const COLUMNS: GridColDef<ScheduledWorkflowDeliverableListItem>[] = [
  {
    field: "name",
    headerName: "Name",
    flex: 1,
    minWidth: 180,
    valueGetter: (_value, row) => reportEntryLabel(row, VIEWER_ZONE),
    sortComparator: (_valueA, _valueB, paramsA, paramsB) => {
      const rowA: ScheduledWorkflowDeliverableListItem = paramsA.api.getRow(paramsA.id)
      const rowB: ScheduledWorkflowDeliverableListItem = paramsB.api.getRow(paramsB.id)
      return rowA.workflowName.localeCompare(rowB.workflowName) || rowA.createdAt.localeCompare(rowB.createdAt)
    },
    renderCell: (params: GridRenderCellParams<ScheduledWorkflowDeliverableListItem, string>) => (
      params.row.read
        ? params.value
        : (
          <Box sx={UNREAD_NAME_SX}>
            <Box sx={UNREAD_DOT_SX} aria-hidden data-testid="report-unread-dot" />
            <Typography variant="body2" noWrap sx={UNREAD_TEXT_SX}><Box component="span" sx={visuallyHidden}>Unread: </Box>{params.value}</Typography>
          </Box>
        )
    ),
  },
  {
    field: "createdAt",
    headerName: "Created",
    flex: 0.5,
    minWidth: 110,
    valueGetter: (_value, row) => row.createdAt,
    renderCell: (params: GridRenderCellParams<ScheduledWorkflowDeliverableListItem, string>) => <MessageTime date={params.value ?? ""} />,
  },
]

function getRowId(row: ScheduledWorkflowDeliverableListItem): string {
  return row.deliverableId
}

export function ScheduledReportsList() {
  const { data, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage, isFetchNextPageError } = useScheduledWorkflowDeliverables()
  const rows = useMemo(() => data?.pages.flatMap(page => page.deliverables) ?? EMPTY_DELIVERABLES, [data])
  const openReport = useOpenReport()
  const isErrored = isError && data == null
  const gridApiRef = useGridApiRef()
  const wasFetchingNextPage = useRef(false)
  const isPagingByUser = useRef(false)
  const pagerButtonRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    // The pager button is disabled or swapped while paging, which drops focus to the body.
    if (wasFetchingNextPage.current && !isFetchingNextPage && isPagingByUser.current) {
      isPagingByUser.current = false
      const focusedElement = document.activeElement
      const hasFocusElsewhere = focusedElement != null && focusedElement !== document.body && focusedElement !== pagerButtonRef.current
      if (hasFocusElsewhere) return
      if (hasNextPage) {
        pagerButtonRef.current?.focus()
      } else {
        const lastRowId = gridExpandedSortedRowIdsSelector(gridApiRef).at(-1)
        if (lastRowId != null) gridApiRef.current?.setCellFocus(lastRowId, "name")
      }
    }
    wasFetchingNextPage.current = isFetchingNextPage
  }, [isFetchingNextPage, hasNextPage, gridApiRef])

  const fetchNextPageFromButton = () => {
    isPagingByUser.current = true
    void fetchNextPage()
  }

  return (
    <>
      {isErrored && (
        <Alert severity="error" data-testid="chatbox-artifacts-error">Couldn&apos;t load your reports. Please try again later.</Alert>
      )}
      {isLoading && !isErrored && (
        <Box sx={CENTER_SX} data-testid="chatbox-artifacts-loading">
          <CircularProgress size={20} aria-label="Loading reports" />
        </Box>
      )}
      {!isLoading && !isErrored && rows.length === 0 && (
        <Box sx={EMPTY_SX} data-testid="chatbox-artifacts-empty">
          <GridViewOutlined sx={EMPTY_GLYPH_SX} />
          <Typography variant="subtitle2">{EMPTY_COPY.scheduled.heading}</Typography>
          <Typography variant="body2" sx={EMPTY_BODY_SX}>{EMPTY_COPY.scheduled.body}</Typography>
        </Box>
      )}
      {!isLoading && !isErrored && rows.length > 0 && (
        <DataGridPremium
          rows={rows}
          columns={COLUMNS}
          apiRef={gridApiRef}
          getRowId={getRowId}
          density="compact"
          disableRowSelectionOnClick
          disableColumnMenu
          disableColumnResize
          hideFooter
          sx={GRID_SX}
          slots={{ toolbar: ArtifactsToolbar }}
          slotProps={{ toolbar: TOOLBAR_PROPS }}
          showToolbar
          initialState={{ sorting: { sortModel: [{ field: "createdAt", sort: "desc" }] } }}
          onRowClick={(params) => openReport(params.row.deliverableId)}
          data-testid="chatbox-artifacts-grid"
        />
      )}
      {!isErrored && hasNextPage && isFetchNextPageError && (
        <Alert severity="error" action={<Button ref={pagerButtonRef} size="small" disabled={isFetchingNextPage} onClick={fetchNextPageFromButton}>Try again</Button>}>Couldn&apos;t load more reports.</Alert>
      )}
      {!isErrored && hasNextPage && !isFetchNextPageError && (
        <SectionMoreButton ref={pagerButtonRef} disabled={isFetchingNextPage} onClick={fetchNextPageFromButton} testId="chatbox-artifacts-scheduled-show-more">
          Show more
        </SectionMoreButton>
      )}
    </>
  )
}
